import { cva, type VariantProps } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

const bannerVariants = cva("flex items-start gap-3 rounded-lg border px-4 py-3 text-base", {
  variants: {
    tone: {
      info: "border-info bg-info-subtle text-info-fg",
      success: "border-success bg-success-subtle text-success-fg",
      warning: "border-warning bg-warning-subtle text-warning-fg",
      danger: "border-danger-muted bg-danger-subtle text-danger-fg",
      brand: "border-secondary-muted bg-secondary-subtle text-secondary-subtle-fg",
    },
  },
  defaultVariants: { tone: "info" },
});

const ICONS = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: XCircle, brand: Info };

/** Inline message. `danger` announces as an alert; the rest as a polite status. */
export function Banner({
  tone,
  title,
  children,
  action,
  className,
}: VariantProps<typeof bannerVariants> & { title?: ReactNode; children?: ReactNode; action?: ReactNode; className?: string }) {
  const Icon = ICONS[tone ?? "info"];
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={cn(bannerVariants({ tone }), className)}>
      <Icon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div>{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
